import type { ProjectCategoryAttachmentInfo } from "@/types/project-category-attachment";
import { getAttachmentColorClasses, getProjectCategoryAttachmentInfo } from "@/types/project-category-attachment";
import { cn } from "@/utils/cn";
import {
	IconAward,
	IconClipboardCheck,
	IconFileDescription,
	IconFileText,
	IconPlayerPlay,
	IconReceipt,
	IconShieldCheck,
	IconShoppingCart,
	IconTruck,
} from "@tabler/icons-react";

interface AttachmentTypeBadgeProps {
	type: string;
	size?: "sm" | "md" | "lg";
	showIcon?: boolean;
	showLabel?: boolean;
	showDescription?: boolean;
	className?: string;
}

const iconComponents = {
	IconClipboardCheck,
	IconAward,
	IconPlayerPlay,
	IconShoppingCart,
	IconTruck,
	IconReceipt,
	IconFileText,
	IconFileDescription,
	IconShieldCheck,
};

export const AttachmentTypeBadge: React.FC<AttachmentTypeBadgeProps> = ({
	type,
	size = "md",
	showIcon = true,
	showLabel = true,
	showDescription = false,
	className,
}) => {
	const attachmentInfo = getProjectCategoryAttachmentInfo(type);

	if (!attachmentInfo) {
		return <span className={cn("px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded", className)}>Unknown</span>;
	}

	const IconComponent = iconComponents[attachmentInfo.icon as keyof typeof iconComponents] || IconFileText;
	const colorClasses = getAttachmentColorClasses(attachmentInfo.color);

	const sizeClasses = {
		sm: "px-2 py-1 text-xs",
		md: "px-3 py-1 text-sm",
		lg: "px-4 py-2 text-base",
	};

	const iconSizes = {
		sm: 12,
		md: 16,
		lg: 20,
	};

	return (
		<div
			className={cn(
				"inline-flex items-center gap-1 rounded-full font-medium",
				sizeClasses[size],
				colorClasses.bg,
				colorClasses.text,
				colorClasses.border,
				"border",
				className,
			)}
		>
			{showIcon && <IconComponent size={iconSizes[size]} className="flex-shrink-0" />}
			{showLabel && <span className="truncate">{attachmentInfo.label}</span>}
			{showDescription && <span className="text-xs opacity-75 truncate ml-1">{attachmentInfo.description}</span>}
		</div>
	);
};

interface AttachmentTypeSelectProps {
	value?: string;
	onChange: (value: string) => void;
	placeholder?: string;
	attachmentTypes?: ProjectCategoryAttachmentInfo[];
	className?: string;
	disabled?: boolean;
}

export const AttachmentTypeSelect: React.FC<AttachmentTypeSelectProps> = ({
	value,
	onChange,
	placeholder = "Select attachment type...",
	attachmentTypes,
	className,
	disabled = false,
}) => {
	// If no attachment types provided, we could use the hook here
	// const { data: defaultAttachmentTypes } = useProjectCategoryAttachments();
	// const types = attachmentTypes || defaultAttachmentTypes || [];

	const types = attachmentTypes || [];

	return (
		<select
			value={value || ""}
			onChange={(e) => onChange(e.target.value)}
			disabled={disabled}
			className={cn(
				"block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm",
				"focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
				"disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
				className,
			)}
		>
			<option value="">{placeholder}</option>
			{types.map((type) => (
				<option key={type.value} value={type.value}>
					{type.label}
				</option>
			))}
		</select>
	);
};

interface AttachmentTypeListProps {
	types: string[];
	size?: "sm" | "md" | "lg";
	showIcon?: boolean;
	showLabel?: boolean;
	className?: string;
	maxItems?: number;
}

export const AttachmentTypeList: React.FC<AttachmentTypeListProps> = ({ types, size = "sm", showIcon = true, showLabel = true, className, maxItems }) => {
	const displayTypes = maxItems ? types.slice(0, maxItems) : types;
	const remainingCount = maxItems && types.length > maxItems ? types.length - maxItems : 0;

	return (
		<div className={cn("flex flex-wrap gap-1", className)}>
			{displayTypes.map((type) => (
				<AttachmentTypeBadge key={type} type={type} size={size} showIcon={showIcon} showLabel={showLabel} />
			))}
			{remainingCount > 0 && (
				<span className={cn("inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full", "font-medium")}>+{remainingCount} more</span>
			)}
		</div>
	);
};
